import React, { useEffect, useState } from 'react';
import { ShieldCheck, Copy, AlertCircle } from 'lucide-react';
import api from '../../../services/api';

/**
 * Violations FMCSA has stopped publishing for a carrier.
 *
 * Two kinds land here and they mean opposite things, so the UI keeps them
 * visually apart rather than showing one "removed" count:
 *
 *   absent_from_source     FMCSA dropped it, almost always a won DataQs
 *                          challenge. These are the cases worth studying.
 *   superseded_by_reissue  FMCSA re-published the same inspection line under a
 *                          new id after a correction. A duplicate, not a win.
 *
 * Only won disputes contribute to the weight total -- counting duplicates there
 * would tell a carrier it shed points it never carried.
 */

export interface RemovedViolation {
  violationId: string;
  inspectionId: string;
  reportNumber: string;
  inspectionDate: string;
  code: string;
  description: string | null;
  wasOos: boolean;
  removalReason: 'absent_from_source' | 'superseded_by_reissue';
  removedAt: string;
  supersededBy: string | null;
  basicId: string | null;
  severityWeight: number | null;
  timeWeight: number;
  weightRemoved: number | null;
}

export interface RemovedViolationsSummary {
  total: number;
  disputesWon: number;
  duplicatesSuperseded: number;
  totalWeightRemoved: number;
}

export interface RemovedViolationsProps {
  dotNumber: number;
}

const BASIC_NAMES: Record<string, string> = {
  'BASIC-11': 'Unsafe Driving',
  'BASIC-12': 'Hours of Service',
  'BASIC-13': 'Driver Fitness',
  'BASIC-14': 'Controlled Substances',
  'BASIC-15': 'Vehicle Maintenance',
  'BASIC-16': 'Crash Indicator',
  'BASIC-17': 'Hazmat Compliance',
};

const fmtDate = (iso: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

export const RemovedViolations: React.FC<RemovedViolationsProps> = ({ dotNumber }) => {
  const [rows, setRows] = useState<RemovedViolation[]>([]);
  const [summary, setSummary] = useState<RemovedViolationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'absent_from_source' | 'superseded_by_reissue'>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get(`/carriers/${dotNumber}/violations/removed`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data as { removed?: RemovedViolation[]; summary?: RemovedViolationsSummary };
        setRows(data?.removed ?? []);
        setSummary(data?.summary ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load removed violations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dotNumber]);

  const visible = filter === 'all' ? rows : rows.filter((r) => r.removalReason === filter);

  if (loading) {
    return <div className="p-6 text-gray-600">Loading removed violations…</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Removed Violations</h2>
        <p className="mt-1 text-sm text-gray-600">
          Violations FMCSA no longer publishes for this carrier. We keep them after they leave
          FMCSA&apos;s file so the dispute history stays visible — they are not counted in any BASIC score.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 text-green-700">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-sm font-medium">Disputes won</span>
            </div>
            <div className="mt-1 text-3xl font-bold text-green-700">{summary.disputesWon}</div>
            <div className="mt-1 text-xs text-green-700">dropped by FMCSA</div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="text-sm font-medium text-blue-700">Severity weight shed</div>
            <div className="mt-1 text-3xl font-bold text-blue-700">{summary.totalWeightRemoved}</div>
            <div className="mt-1 text-xs text-blue-700">from won disputes only</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center gap-2 text-gray-700">
              <Copy className="h-5 w-5" />
              <span className="text-sm font-medium">Superseded duplicates</span>
            </div>
            <div className="mt-1 text-3xl font-bold text-gray-700">{summary.duplicatesSuperseded}</div>
            <div className="mt-1 text-xs text-gray-600">re-issued, not disputes</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {([
          ['all', 'All'],
          ['absent_from_source', 'Disputes won'],
          ['superseded_by_reissue', 'Superseded duplicates'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
          No removed violations recorded for this carrier yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Inspection Date', 'Report #', 'Code', 'BASIC', 'Severity', 'Weight Removed', 'Outcome', 'Removed On'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visible.map((r) => {
                const won = r.removalReason === 'absent_from_source';
                return (
                  <tr key={r.violationId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                      {fmtDate(r.inspectionDate)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-gray-700">
                      {r.reportNumber || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-mono font-medium text-gray-900">{r.code}</div>
                      {r.description && (
                        <div className="mt-0.5 max-w-md text-xs text-gray-600">{r.description}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {r.basicId ? BASIC_NAMES[r.basicId] || r.basicId : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {r.severityWeight ?? '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">
                      {won && r.weightRemoved != null ? r.weightRemoved : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {won ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Dispute won
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                          title={r.supersededBy ? `Replaced by violation ${r.supersededBy}` : undefined}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Superseded
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {fmtDate(r.removedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RemovedViolations;
